import { useCallback, useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import {
  availabilityCollectionPeriodsSelectedPeriodSelector,
  availableCollectionPeriodToEmbeddedTimeSlot,
  updatedSelectedPeriod,
} from 'slices/availabilityCollectionPeriod.slice';
import { availabilityCollectionPeriodsTimeslotsSelector } from 'slices/availabilityCollectionTimeslot.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type {
  TAvailabilityCollectionEmbeddedTimeSlot,
  TAvailabilityCollectionPeriod,
} from 'types/availabilityCollectionPeriod.type';
import type { TAvailabilityCollectionTimeslot } from 'types/availabilityCollectionTimeslot.type';
import {
  EditableTable,
  TEDrawerSection,
  TEInput,
} from '@timeedit/ui-components';
import { DatePicker, Form, Space } from '@timeedit/ui-components/antd';
import { timeslotColumns } from '../Timeslots/timeslotUtils';

const PeriodDetail = () => {
  const dispatch = useAppDispatch();
  const selectedPeriod = useAppSelector(
    availabilityCollectionPeriodsSelectedPeriodSelector
  );

  const timeslots = useAppSelector(
    availabilityCollectionPeriodsTimeslotsSelector
  );

  const onChange = (partialObject: Partial<TAvailabilityCollectionPeriod>) => {
    dispatch(updatedSelectedPeriod(partialObject));
  };

  const onChangeDebounce = debounce(onChange, 400);

  const onChangePeriodTime = useCallback(
    (values: [Dayjs, Dayjs]) => {
      let periodStart;
      let periodEnd;

      if (values[0] && values[1]) {
        periodStart = values[0].format('YYYY-MM-DD');
        periodEnd = values[1].format('YYYY-MM-DD');
      }

      dispatch(
        updatedSelectedPeriod({
          periodStart,
          periodEnd,
        })
      );
    },
    [dispatch]
  );

  const onChangeExpirationTime = useCallback(
    (date: Dayjs) => {
      dispatch(
        updatedSelectedPeriod({
          periodDeadline: date?.format('YYYY-MM-DD') || null,
        })
      );
    },
    [dispatch]
  );

  const { periodStart, periodEnd, periodDeadline } = useMemo(() => {
    const periodStart = selectedPeriod?.periodStart
      ? dayjs(selectedPeriod?.periodStart)
      : null;
    const periodEnd = selectedPeriod?.periodEnd
      ? dayjs(selectedPeriod?.periodEnd)
      : null;
    const periodDeadline = selectedPeriod?.periodDeadline
      ? dayjs(selectedPeriod?.periodDeadline)
      : null;

    return {
      periodStart,
      periodEnd,
      periodDeadline,
    };
  }, [selectedPeriod]);

  const footer = useCallback(
    () => (
      <div>
        {selectedPeriod?.timeSlots?.length
          ? `${selectedPeriod?.timeSlots?.length} timeslots selected`
          : ''}
      </div>
    ),
    [selectedPeriod?.timeSlots]
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedPeriod?.timeSlots?.map((slot) => slot._id) ?? [],
      onChange: (_, records: TAvailabilityCollectionTimeslot[]) => {
        onChange({
          timeSlots: records.map(availableCollectionPeriodToEmbeddedTimeSlot),
        });
      },
    }),
    [selectedPeriod?.timeSlots, onChange]
  );

  return (
    <>
      <TEDrawerSection label="General">
        <Space direction="vertical" style={{ width: '100%' }}>
          <TEInput.Input
            value={selectedPeriod?.name as string}
            label="Name"
            onKeyUp={(e) => onChangeDebounce({ name: e.currentTarget.value })}
            tooltip="Preferred name for the timeslot"
          />
          <TEInput.TextArea
            value={selectedPeriod?.description as string}
            label="Description"
            onKeyUp={(e) =>
              onChangeDebounce({ description: e.currentTarget.value })
            }
            tooltip="Description"
          />
        </Space>
      </TEDrawerSection>
      <TEDrawerSection label="Detail">
        <Space direction="vertical">
          <Form.Item label="Period date">
            <DatePicker.RangePicker
              format="YYYY-MM-DD"
              minDate={dayjs()}
              value={[periodStart, periodEnd]}
              onChange={onChangePeriodTime}
            />
          </Form.Item>
          <Form.Item label="Expiration date">
            <DatePicker
              format="YYYY-MM-DD"
              minDate={dayjs()}
              maxDate={periodStart}
              value={periodDeadline}
              onChange={onChangeExpirationTime}
            />
          </Form.Item>
        </Space>
      </TEDrawerSection>
      <TEDrawerSection label="Timeslots">
        <EditableTable<
          | TAvailabilityCollectionTimeslot
          | TAvailabilityCollectionEmbeddedTimeSlot
        >
          rowKey="_id"
          columns={timeslotColumns}
          dataSource={timeslots}
          pagination={false}
          footer={footer}
          rowSelection={rowSelection}
        />
      </TEDrawerSection>
    </>
  );
};

export default PeriodDetail;
